import { useMutation } from '@tanstack/react-query'
import { createFileRoute, Link, useNavigate } from '@tanstack/react-router'
import { ArrowLeft, Loader2, Plus } from 'lucide-react'
import { useState } from 'react'
import { toast } from 'sonner'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Textarea } from '@/components/ui/textarea'
import { orpc } from '@/lib/orpcClient'

export const Route = createFileRoute('/settings/roles/new')({
  component: NewRolePage,
})

function NewRolePage() {
  const navigate = useNavigate()
  const [key, setKey] = useState('')
  const [name, setName] = useState('')
  const [description, setDescription] = useState('')

  const createMut = useMutation(
    orpc.rbac.roles.create.mutationOptions({
      onSuccess: (result: unknown) => {
        const { id } = result as { id: string }
        toast.success('Role created')
        // Navigate into the editor so the admin can set grants next.
        navigate({ to: '/settings/roles/$roleKey', params: { roleKey: id } })
      },
      onError: (e: Error) => toast.error(e.message),
    }),
  )

  // `key` must be a bare lowercase slug. Enforce client-side so the
  // form surfaces the rule before the backend rejects it — the oRPC
  // input enforces the same regex.
  const keyValid = key === '' ? false : /^[a-z][a-z0-9_]*$/.test(key)
  const nameValid = name.trim().length > 0
  const canSubmit = keyValid && nameValid && !createMut.isPending

  return (
    <div className="container mx-auto py-6 px-4 max-w-2xl">
      <Button asChild variant="ghost" size="sm" className="mb-2 -ml-2">
        <Link to="/settings/roles">
          <ArrowLeft className="h-3.5 w-3.5 mr-1" />
          Back to roles
        </Link>
      </Button>
      <h1 className="text-2xl font-bold mb-6">Create role</h1>

      <Card>
        <CardHeader>
          <CardTitle className="text-base">Role details</CardTitle>
          <CardDescription>
            After the role exists you'll be taken to the editor to pick which module actions and
            fields it grants.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div>
            <Label htmlFor="role-key">Key</Label>
            <Input
              id="role-key"
              value={key}
              onChange={(e) => setKey(e.target.value.toLowerCase())}
              placeholder="e.g. sales_lead"
              maxLength={64}
            />
            <p className="text-xs text-muted-foreground mt-1">
              Lowercase letters, numbers, and underscores. Used as the stable identifier in the DB;
              cannot be renamed after creation.
            </p>
            {key !== '' && !keyValid && (
              <p className="text-xs text-destructive mt-1">
                Must start with a letter and contain only a-z, 0-9, _
              </p>
            )}
          </div>
          <div>
            <Label htmlFor="role-name">Name</Label>
            <Input
              id="role-name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="Sales Lead"
            />
          </div>
          <div>
            <Label htmlFor="role-description">Description</Label>
            <Textarea
              id="role-description"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="Optional — what this role is for."
              rows={3}
            />
          </div>
          <div className="flex justify-end gap-2 pt-2">
            <Button asChild variant="outline">
              <Link to="/settings/roles">Cancel</Link>
            </Button>
            <Button
              disabled={!canSubmit}
              onClick={() =>
                createMut.mutate({
                  key,
                  name: name.trim(),
                  ...(description.trim() ? { description: description.trim() } : {}),
                })
              }
            >
              {createMut.isPending ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <>
                  <Plus className="h-4 w-4 mr-2" />
                  Create role
                </>
              )}
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  )
}
